import React from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  X,
  Shield,
  AlertCircle,
  Calendar,
  Mail,
  Phone,
  User,
} from "lucide-react";

const AdminPermissionsModal = ({ isOpen, admin, onClose }) => {
  if (!isOpen || !admin) return null;

  // Calculate total permissions
  const getTotalPermissions = (permissions) => {
    if (!permissions || typeof permissions !== "object") return 0;
    return Object.values(permissions).reduce((total, modulePermissions) => {
      return (
        total +
        (Array.isArray(modulePermissions) ? modulePermissions.length : 0)
      );
    }, 0);
  };

  const totalPermissions = getTotalPermissions(admin.permissions);
  const hasPermissions =
    admin.permissions &&
    typeof admin.permissions === "object" &&
    Object.keys(admin.permissions).length > 0;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex justify-between items-center p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
            <div className="flex gap-3 items-center">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow-sm">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Admin Permissions
                </h3>
                <p className="text-sm text-gray-600">
                  {admin.first_name} {admin.last_name} • {totalPermissions}{" "}
                  total permissions
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 bg-white rounded-lg border border-gray-200 shadow-sm transition-all duration-200 hover:text-gray-700 hover:bg-gray-50 hover:border-gray-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Modal Body */}
          <div className="overflow-y-auto flex-1 p-6">
            {/* Admin Info */}
            <div className="p-4 mb-6 bg-gray-50 rounded-xl border border-gray-200">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex gap-3 items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <User className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Name</p>
                    <p className="text-gray-900">
                      {admin.first_name} {admin.last_name}
                    </p>
                  </div>
                </div>
                <div className="flex gap-3 items-center">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Shield className="w-4 h-4 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Role</p>
                    <p className="text-gray-900">
                      {admin.role || "Administrator"}
                    </p>
                  </div>
                </div>
                <div className="flex gap-3 items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Mail className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Email</p>
                    <p className="text-gray-900">{admin.email}</p>
                  </div>
                </div>
                <div className="flex gap-3 items-center">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Phone className="w-4 h-4 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Phone</p>
                    <p className="text-gray-900">
                      {admin.phone_number || "Not provided"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Permissions Section */}
            <div>
              <h4 className="mb-4 text-lg font-semibold text-gray-900">
                Permissions Breakdown
              </h4>

              {hasPermissions ? (
                <div className="space-y-4">
                  {Object.entries(admin.permissions).map(
                    ([module, permissions], moduleIndex) => (
                      <motion.div
                        key={module}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: moduleIndex * 0.1 }}
                        className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200"
                      >
                        <div className="flex justify-between items-center mb-3">
                          <h5 className="font-semibold text-gray-900">
                            {module.replace(/_/g, " ")}
                          </h5>
                          <span className="px-3 py-1 text-sm font-medium text-purple-600 bg-purple-100 rounded-full">
                            {Array.isArray(permissions)
                              ? permissions.length
                              : 0}{" "}
                            permissions
                          </span>
                        </div>

                        {Array.isArray(permissions) &&
                        permissions.length > 0 ? (
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                            {permissions.map((permission, permIndex) => (
                              <motion.div
                                key={permIndex}
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{
                                  delay: moduleIndex * 0.1 + permIndex * 0.05,
                                }}
                                className="flex gap-2 items-center p-2 bg-white rounded-lg border border-purple-100 shadow-sm"
                              >
                                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                                <span className="text-sm font-medium text-gray-700">
                                  {permission.replace(/_/g, " ")}
                                </span>
                              </motion.div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm italic text-gray-500">
                            No permissions in this module
                          </p>
                        )}
                      </motion.div>
                    )
                  )}
                </div>
              ) : (
                <div className="py-8 text-center">
                  <div className="flex justify-center items-center p-4 mx-auto mb-4 w-16 h-16 bg-orange-100 rounded-full">
                    <AlertCircle className="w-8 h-8 text-orange-600" />
                  </div>
                  <h4 className="mb-2 text-lg font-medium text-gray-900">
                    No Permissions Assigned
                  </h4>
                  <p className="text-gray-600">
                    This administrator doesn't have any permissions assigned
                    yet.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex flex-shrink-0 justify-between items-center p-6 bg-gray-50 border-t border-gray-200">
            <div className="flex gap-4 items-center text-sm text-gray-600">
              <div className="flex gap-2 items-center">
                <Calendar className="w-4 h-4" />
                <span>
                  Created: {new Date(admin.created_at).toLocaleDateString()}
                </span>
              </div>
              <div className="flex gap-2 items-center">
                <div
                  className={`w-3 h-3 rounded-full ${
                    admin.status === 1 ||
                    admin.status === "1" ||
                    admin.status === "active"
                      ? "bg-red-500"
                      : "bg-orange-500"
                  }`}
                ></div>
                <span className="capitalize">
                  {admin.status === 1 ||
                  admin.status === "1" ||
                  admin.status === "active"
                    ? "Active"
                    : "Inactive"}
                </span>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-all duration-200 hover:bg-gray-50 hover:border-gray-400 shadow-sm"
              >
                Close
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AdminPermissionsModal;
